import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VideoPlayer from '../components/VideoPlayer';
import AuthModal from '../components/AuthModal';
import api from '../services/api';
import { API_ENDPOINTS, API_URL } from '../config/api';

/** Installments UI when API/DB has this on (handles null, snake_case, string/number booleans). */
function parseInstallmentEnabled(data) {
  if (!data) return false;
  const v = data.installmentEnabled ?? data.installment_enabled;
  if (v === true || v === 1 || v === '1') return true;
  if (typeof v === 'string' && v.trim().toLowerCase() === 'true') return true;
  return false;
}

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin: checkIsAdmin } = useAuth();
  const isAdminUser = checkIsAdmin() || (user && user.role === 'ADMIN');
  
  // GST calculation helper
  const GST_RATE = 0.18; // 18%
  const calculateGST = (price) => {
    return parseFloat((price * GST_RATE).toFixed(2));
  };
  const calculateTotal = (price) => {
    return parseFloat((price + calculateGST(price)).toFixed(2));
  };
  
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions] = useState({});
  const [contents] = useState({});
  const [liveSessions] = useState({}); // Store by content ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [loadingSections] = useState(new Set()); // Track which sections are loading
  const [visibleSectionsCount, setVisibleSectionsCount] = useState(5); // Only show first 5 sections initially
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState('');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [checkoutPaymentMode, setCheckoutPaymentMode] = useState('FULL'); // FULL | INSTALLMENT (first payment only)
  const [installmentProgress, setInstallmentProgress] = useState(null); // from enrollment check when enrolled
  const showSectionContentOnPublicPage = false;

  const instructorsForDisplay = useMemo(() => {
    if (!course) return [];
    if (Array.isArray(course.courseInstructors) && course.courseInstructors.length > 0) {
      return [...course.courseInstructors].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    if (
      course.instructor ||
      course.instructorBio ||
      course.instructorTitle ||
      course.instructorImageUrl ||
      course.instructorLinkedIn
    ) {
      return [
        {
          displayName: course.instructor,
          title: course.instructorTitle,
          bio: course.instructorBio,
          imageUrl: course.instructorImageUrl,
          linkedIn: course.instructorLinkedIn
        }
      ];
    }
    return [];
  }, [course]);

  useEffect(() => {
    fetchCourseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showPurchaseModal || showLoginModal || showAuthModal || showPaymentSuccess) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showPurchaseModal, showLoginModal, showAuthModal, showPaymentSuccess]);

  useEffect(() => {
    if (isAuthenticated() && course) {
      checkEnrollmentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  const fetchCourseDetails = async () => {
    try {
      let courseData = null; // Declare outside to use later
      
      // Fetch course details
      const courseResponse = await fetch(API_ENDPOINTS.COURSES.BY_ID(id));
      if (courseResponse.ok) {
        courseData = await courseResponse.json();
        
        // Parse string fields to arrays
        if (courseData.learningOutcomes && typeof courseData.learningOutcomes === 'string') {
          try {
            courseData.learningOutcomes = JSON.parse(courseData.learningOutcomes);
          } catch (e) {
            courseData.learningOutcomes = courseData.learningOutcomes.split('|').filter(item => item.trim());
          }
        }
        
        if (courseData.requirements && typeof courseData.requirements === 'string') {
          try {
            courseData.requirements = JSON.parse(courseData.requirements);
          } catch (e) {
            courseData.requirements = courseData.requirements.split('|').filter(item => item.trim());
          }
        }
        
        if (courseData.targetAudience && typeof courseData.targetAudience === 'string') {
          try {
            courseData.targetAudience = JSON.parse(courseData.targetAudience);
          } catch (e) {
            courseData.targetAudience = courseData.targetAudience.split('|').filter(item => item.trim());
          }
        }

        courseData.installmentEnabled = parseInstallmentEnabled(courseData);

        setCourse(courseData);
      } else {
        setError('Course not found');
        setLoading(false);
        return;
      }

      // Fetch course sections only (lazy load content when expanded)
      const sectionsResponse = await fetch(API_ENDPOINTS.COURSES.SECTIONS(id));
      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData);
        // For live courses, only show first 3 sections initially to speed up load
        if (courseData && courseData.courseType === 'LIVE') {
          setVisibleSectionsCount(3);
        } else {
          // For recorded courses, show first 5 sections
          setVisibleSectionsCount(5);
        }
        // Don't fetch questions/contents here - load them when sections are expanded
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching course details:', error);
      setError('Unable to load course details');
      setLoading(false);
    }
  };

  const checkEnrollmentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsEnrolled(false);
        setInstallmentProgress(null);
        return;
      }

      const response = await api.get(`/api/course-enrollments/check/${id}`);
      const data = response.data || {};
      setIsEnrolled(!!data.enrolled);
      if (data.enrolled) {
        setInstallmentProgress({
          coursePaymentPlan: data.coursePaymentPlan || 'FULL',
          installmentsTotal: data.installmentsTotal,
          installmentsPaid: data.installmentsPaid
        });
      } else {
        setInstallmentProgress(null);
      }
    } catch (error) {
      console.error('Error checking enrollment status:', error);
      setIsEnrolled(false);
      setInstallmentProgress(null);
    }
  };

  const handlePurchaseClick = () => {
    if (!isAuthenticated()) {
      setShowLoginModal(true);
      return;
    }
    setCheckoutPaymentMode('FULL');
    setShowPurchaseModal(true);
  };

  /** Mirrors backend CourseInstallmentHelper: registration first (optional), then equal splits of remainder. */
  const splitPaiseToAmounts = (totalPaise, n) => {
    const base = Math.floor(totalPaise / n);
    const rem = totalPaise % n;
    const amounts = [];
    for (let i = 0; i < n; i += 1) {
      const slice = base + (i === n - 1 ? rem : 0);
      amounts.push(slice / 100);
    }
    return amounts;
  };

  const buildCoursePaymentSchedule = (c) => {
    const totalWithGst = parseFloat(calculateTotal(c.price));
    const tPaise = Math.round(totalWithGst * 100);
    const regParsed =
      c.registrationFee != null ? parseFloat(String(c.registrationFee)) : NaN;
    const reg = !Number.isNaN(regParsed) && regParsed > 0 ? regParsed : 0;
    if (!reg || reg <= 0) {
      const n = Math.min(24, Math.max(2, parseInt(c.installmentCount, 10) || 3));
      return splitPaiseToAmounts(tPaise, n);
    }
    const nAfter = Math.min(24, Math.max(1, parseInt(c.installmentCount, 10) || 1));
    let rPaise = Math.round(reg * 100);
    rPaise = Math.min(rPaise, tPaise - 1);
    const rem = tPaise - rPaise;
    const after = splitPaiseToAmounts(rem, nAfter);
    return [rPaise / 100, ...after];
  };

  const hasRegistrationFee = (c) => {
    if (!c || c.registrationFee == null) return false;
    const v = parseFloat(String(c.registrationFee));
    return !Number.isNaN(v) && v > 0;
  };

  const isInstallmentsEnabled = (c) => parseInstallmentEnabled(c);

  const openRazorpayForCourse = (orderData, courseIdNum) => {
    const options = {
      key: orderData.keyId,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'YCode Universe',
      description: orderData.courseName,
      order_id: orderData.orderId,
      prefill: {
        name: localStorage.getItem('username') || '',
        email: localStorage.getItem('email') || ''
      },
      theme: { color: '#8b5cf6' },
      handler: async function (response) {
        try {
          const verifyResponse = await fetch(`${API_URL}/api/courses/${courseIdNum}/payment/verify`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature
            })
          });

          if (verifyResponse.ok) {
            setPaymentDetails({
              courseName: course.title,
              transactionId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              amount: calculateTotal(course.price),
              isAdmin: false
            });
            setShowPurchaseModal(false);
            setShowPaymentSuccess(true);
            await checkEnrollmentStatus();
          } else {
            const errorData = await verifyResponse.json();
            alert(`Payment verification failed: ${errorData.error}\nPlease contact support with Order ID: ${response.razorpay_order_id}`);
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          alert('Payment completed but verification failed. Please contact support.');
        } finally {
          setEnrolling(false);
        }
      },
      modal: {
        ondismiss: function () {
          alert('Payment cancelled. You can try again anytime.');
          setEnrolling(false);
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handlePayNextInstallment = async () => {
    setEnrolling(true);
    try {
      const orderResponse = await fetch(`${API_URL}/api/courses/${id}/payment/create-next-installment-order`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!orderResponse.ok) {
        const err = await orderResponse.json().catch(() => ({}));
        alert(err.error || 'Could not start payment.');
        setEnrolling(false);
        return;
      }
      const orderData = await orderResponse.json();
      openRazorpayForCourse(orderData, parseInt(id, 10));
    } catch (e) {
      console.error(e);
      alert('Failed to start payment.');
      setEnrolling(false);
    }
  };

  const handlePurchase = async () => {
    setEnrolling(true);
    setEnrollmentError('');

    try {
      setShowPurchaseModal(false);
      
      // Check if user is admin - admins get free access
      if (isAdminUser) {
        // Direct enrollment for admin users
        try {
          const enrollResponse = await api.post('/api/course-enrollments', {
            courseId: parseInt(id),
            userId: user.id
          });
          
          if (enrollResponse.status === 200 || enrollResponse.status === 201) {
            setPaymentDetails({
              courseName: course.title,
              transactionId: 'ADMIN_FREE_ACCESS',
              orderId: 'ADMIN_' + Date.now(),
              amount: 0,
              isAdmin: true
            });
            setShowPurchaseModal(false);
            setShowPaymentSuccess(true);
            await checkEnrollmentStatus();
            return;
          }
        } catch (error) {
          console.error('Admin enrollment error:', error);
        }
      }
      
      const mode =
        isInstallmentsEnabled(course) && checkoutPaymentMode === 'INSTALLMENT' ? 'INSTALLMENT' : 'FULL';
      const orderResponse = await fetch(`${API_URL}/api/courses/${id}/payment/create-order`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ paymentMode: mode })
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        alert(errorData.error || 'Failed to create order. Please try again.');
        setEnrolling(false);
        return;
      }

      const orderData = await orderResponse.json();
      openRazorpayForCourse(orderData, parseInt(id, 10));
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to initiate payment. Please try again.');
      setEnrolling(false);
    }
  };

  const handlePreviewCourse = () => {
    // Scroll to course content and expand all free sections
    const freeSections = sections.filter(section => section.isFree);
    if (freeSections.length > 0) {
      const newExpanded = new Set(expandedSections);
      freeSections.forEach(section => newExpanded.add(section.id));
      setExpandedSections(newExpanded);
      
      // Scroll to curriculum section
      const curriculumSection = document.querySelector('#course-curriculum');
      if (curriculumSection) {
        curriculumSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      alert('No free preview content available for this course. Please enroll to access all content.');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'EASY': return 'text-green-700 bg-green-50 border-green-200';
      case 'MEDIUM': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'HARD': return 'text-red-700 bg-red-50 border-red-200';
      case 'EXPERT': return 'text-purple-700 bg-purple-50 border-purple-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getContentTypeIcon = (contentType) => {
    switch (contentType) {
      case 'VIDEO': return 'Video';
      case 'DOCUMENT': return 'Document';
      case 'QUESTION': return 'Practice';
      default: return 'Content';
    }
  };

  const getContentTypeColor = (contentType) => {
    switch (contentType) {
      case 'VIDEO': return 'text-red-700 bg-red-50 border-red-200';
      case 'DOCUMENT': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'QUESTION': return 'text-green-700 bg-green-50 border-green-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg font-medium">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="bg-gray-900 rounded-lg shadow-sm border border-gray-700 p-8 max-w-md">
            <h2 className="text-xl font-semibold text-white mb-2">Course Not Found</h2>
            <p className="text-gray-300 mb-6">{error}</p>
          <Link
            to="/courses"
              className="bg-white text-black px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium"
          >
            Back to Courses
          </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white">Course not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Breadcrumb */}
      <div className="bg-gray-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/courses" className="text-gray-400 hover:text-white transition-colors">
              Courses
          </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">{course.title}</span>
          </nav>
        </div>
        </div>

        {/* Course Header */}
      <div className="bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Course page image — 4:5 aspect ratio (optional; falls back to listing image) */}
            <div className="lg:col-span-1">
              {(course.pageImageUrl || course.imageUrl) ? (
                <div className="w-full aspect-[4/5] rounded-lg overflow-hidden shadow-sm border border-gray-700 bg-gray-800">
                  <img
                    src={course.pageImageUrl || course.imageUrl}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-full aspect-[4/5] bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                  <div className="text-gray-400 text-4xl font-light">Course Image</div>
                </div>
              )}
            </div>
            
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <span className={`${
                  course.courseType === 'LIVE' 
                    ? 'bg-gradient-to-r from-pink-500 to-red-500' 
                    : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                } text-white px-3 py-1 rounded-md text-sm font-bold shadow-lg`}>
                  {course.courseType === 'LIVE' ? '🔴 LIVE COURSE' : '🎬 RECORDED COURSE'}
                </span>
                <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded-md text-sm font-medium border border-gray-700">
                  {course.category}
                </span>
                {course.isBestSeller && (
                  <span className="bg-yellow-600 text-white px-3 py-1 rounded-md text-xs font-bold">
                    BESTSELLER
                  </span>
                )}
                {course.courseType === 'LIVE' && course.isCohortBased && (
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1 rounded-md text-xs font-bold">
                    🎓 Cohort {course.cohortNumber || 1}
                  </span>
                )}
                {/* Discount Badge */}
                {course.isOnSale && course.discountPercentage && (
                  <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-md text-xs font-bold animate-pulse shadow-lg">
                    🔥 {course.discountPercentage}% OFF
                  </span>
                )}
                {course.isOnSale && course.offerDescription && (
                  <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-md text-xs font-bold">
                    ⚡ {course.offerDescription}
                  </span>
                )}
              </div>
              
              <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
                {course.title}
              </h1>
              
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                {course.description}
              </p>
              
              {/* Live Course Alert */}
              {course.courseType === 'LIVE' && (
                <div className="mb-6 p-3 sm:p-5 md:p-6 bg-gradient-to-r from-pink-500/20 to-red-500/20 border-2 border-pink-500/50 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                    <span className="text-xl sm:text-2xl md:text-3xl flex-shrink-0 animate-pulse">🔴</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 md:mb-4">Live Interactive Course</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm md:text-base">
                        {course.startDate && (
                          <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-md p-2 sm:p-2.5 md:p-3">
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">📅</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-400 text-xs sm:text-sm block mb-0.5">Starts</span>
                              <strong className="text-white text-xs sm:text-sm md:text-base block">{new Date(course.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                            </div>
                          </div>
                        )}
                        {course.endDate && (
                          <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-md p-2 sm:p-2.5 md:p-3">
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">🏁</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-400 text-xs sm:text-sm block mb-0.5">Ends</span>
                              <strong className="text-white text-xs sm:text-sm md:text-base block">{new Date(course.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                            </div>
                          </div>
                        )}
                        {course.sessionFrequency && (
                          <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-md p-2 sm:p-2.5 md:p-3">
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">📆</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-400 text-xs sm:text-sm block mb-0.5">Schedule</span>
                              <strong className="text-white text-xs sm:text-sm md:text-base block">{course.sessionFrequency}</strong>
                            </div>
                          </div>
                        )}
                        {course.totalSessions && (
                          <div className="flex items-center gap-2 text-gray-300 bg-white/5 rounded-md p-2 sm:p-2.5 md:p-3">
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">🎯</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-gray-400 text-xs sm:text-sm block mb-0.5">Total Sessions</span>
                              <strong className="text-white text-xs sm:text-sm md:text-base block">{course.totalSessions} live sessions</strong>
                            </div>
                          </div>
                        )}
                        {course.maxStudents && (
                          <div className={`flex items-center gap-2 text-gray-300 bg-white/5 rounded-md p-2 sm:p-2.5 md:p-3 ${
                            (course.maxStudents - (course.currentEnrollments || 0)) <= 10 ? 'border-2 border-red-500/50' : ''
                          }`}>
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">👥</span>
                            <div className="flex-1 min-w-0 text-center">
                              <span className="text-gray-400 text-xs sm:text-sm block mb-0.5">Available Seats</span>
                              <div className="flex items-center justify-center gap-2 flex-wrap">
                                <strong className={`text-xs sm:text-sm md:text-base ${
                                  (course.maxStudents - (course.currentEnrollments || 0)) <= 10 ? 'text-red-400 animate-pulse' : 'text-white'
                                }`}>{course.maxStudents - (course.currentEnrollments || 0)}/{course.maxStudents}</strong>
                                {(course.maxStudents - (course.currentEnrollments || 0)) <= 10 && (
                                  <span className="text-red-400 font-bold text-xs sm:text-sm md:text-base">⚠️ Almost Full!</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {course.enrollmentDeadline && new Date(course.enrollmentDeadline) > new Date() && (
                          <div className="flex items-center gap-2 text-orange-300 bg-orange-500/10 rounded-md p-2 sm:p-2.5 md:p-3 border border-orange-500/30 sm:col-span-2">
                            <span className="flex-shrink-0 text-base sm:text-lg md:text-xl">⏰</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-orange-400 text-xs sm:text-sm block mb-0.5">Enrollment Deadline</span>
                              <strong className="text-orange-300 text-xs sm:text-sm md:text-base block">{new Date(course.enrollmentDeadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                            </div>
                          </div>
                        )}
                        {course.enrollmentDeadline && new Date(course.enrollmentDeadline) <= new Date() && (
                          <div className="flex items-center gap-2 text-red-400 bg-red-500/10 rounded-md p-2 sm:p-2.5 md:p-3 border border-red-500/30 font-bold text-xs sm:text-sm md:text-base sm:col-span-2">
                            <span className="text-base sm:text-lg md:text-xl">🔒</span>
                            <span>Enrollment Closed</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              
              {/* Created/Updated Info */}
              <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-400">
                {course.createdAt && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Created {new Date(course.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                {course.lastUpdated && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Last updated {new Date(course.lastUpdated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                {course.language && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span>{course.language}</span>
                  </div>
                )}
              </div>
              
              {course.durationHours && (
                <div className="mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                      <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm font-medium">Duration</div>
                      <div className="text-white font-semibold">{course.durationHours} hours</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Price Display with Discount */}
              <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <div className="space-y-3">
                  {course.isOnSale && course.originalPrice ? (
                    <>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-gray-400 text-sm line-through">₹{course.originalPrice}</span>
                        {course.discountPercentage && (
                          <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-lg text-sm font-bold">
                            Save {course.discountPercentage}%
                          </span>
                        )}
                      </div>
                      <div className="space-y-2 pt-2 border-t border-gray-600">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 text-sm">Price:</span>
                          <span className="text-white text-base font-semibold">₹{course.price}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">GST (18%):</span>
                          <span className="text-gray-400 text-sm">₹{calculateGST(course.price)}</span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-gray-600">
                          <span className="text-white font-semibold text-base">Total:</span>
                          <span className="text-green-400 font-bold text-xl">₹{calculateTotal(course.price)}</span>
                        </div>
                      </div>
                      {course.offerDescription && (
                        <div className="text-orange-400 text-sm font-semibold">
                          ⚡ {course.offerDescription}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2 pt-2 border-t border-gray-600">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">Price:</span>
                        <span className="text-white text-base font-semibold">₹{course.price}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">GST (18%):</span>
                        <span className="text-gray-400 text-sm">₹{calculateGST(course.price)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-600">
                        <span className="text-white font-semibold text-base">Total:</span>
                        <span className="text-green-400 font-bold text-xl">₹{calculateTotal(course.price)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isInstallmentsEnabled(course) && !isEnrolled && (
                <div className="mb-4 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border border-purple-500/40">
                  <p className="text-purple-100 font-semibold text-sm sm:text-base flex items-center gap-2">
                    <span aria-hidden>💳</span>
                    Installment plan available
                  </p>
                  <p className="text-purple-200/90 text-xs sm:text-sm mt-1">
                    {hasRegistrationFee(course)
                      ? `Registration fee plus ${Math.min(24, Math.max(1, parseInt(course.installmentCount, 10) || 1))} further payment${(parseInt(course.installmentCount, 10) || 1) !== 1 ? 's' : ''} (GST-inclusive total). Choose full pay or installments in the next step.`
                      : `Split the GST-inclusive total into ${Math.min(24, Math.max(2, parseInt(course.installmentCount, 10) || 3))} payments. You’ll pick full pay or installments when you enroll.`}
                  </p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                {isEnrolled ? (
                  <>
                    {installmentProgress?.coursePaymentPlan === 'INSTALLMENT' &&
                    installmentProgress.installmentsTotal != null &&
                    installmentProgress.installmentsPaid != null &&
                    installmentProgress.installmentsPaid < installmentProgress.installmentsTotal && (
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <p className="text-amber-200 text-sm">
                          Installment {installmentProgress.installmentsPaid} of {installmentProgress.installmentsTotal} paid — pay the rest from here or your dashboard.
                        </p>
                        <button
                          type="button"
                          onClick={handlePayNextInstallment}
                          disabled={enrolling}
                          className="bg-amber-500 hover:bg-amber-600 text-black px-8 py-4 rounded-lg font-semibold text-lg disabled:opacity-50"
                        >
                          {enrolling ? 'Processing…' : `Pay next installment (${installmentProgress.installmentsPaid + 1}/${installmentProgress.installmentsTotal})`}
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors duration-200 font-semibold text-lg flex items-center gap-2 justify-center"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Go to Dashboard
                    </button>
                  </>
                ) : (
                  <>
                    {/* Check enrollment eligibility for live courses */}
                    {course.courseType === 'LIVE' && course.enrollmentDeadline && new Date(course.enrollmentDeadline) <= new Date() ? (
                      <button 
                        disabled
                        className="bg-gray-600 text-gray-300 px-8 py-4 rounded-lg cursor-not-allowed font-semibold text-lg flex items-center gap-2"
                      >
                        <span>🔒</span>
                        <span>Enrollment Closed</span>
                      </button>
                    ) : course.courseType === 'LIVE' && course.maxStudents && (course.currentEnrollments || 0) >= course.maxStudents ? (
                      <button 
                        disabled
                        className="bg-gray-600 text-gray-300 px-8 py-4 rounded-lg cursor-not-allowed font-semibold text-lg flex items-center gap-2"
                      >
                        <span>⛔</span>
                        <span>Course Full - No Seats Available</span>
                      </button>
                    ) : (
                      <>
                        <button 
                          onClick={handlePurchaseClick}
                          disabled={enrolling}
                          className={`${
                            course.courseType === 'LIVE' 
                              ? 'bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600' 
                              : course.isOnSale
                              ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                              : 'bg-white hover:bg-gray-100'
                          } ${
                            course.courseType === 'LIVE' || course.isOnSale ? 'text-white' : 'text-black'
                          } px-8 py-4 rounded-lg transition-all duration-200 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg`}
                        >
                          {enrolling ? 'Processing...' : (
                            <>
                              <span>{course.courseType === 'LIVE' ? '🔴' : course.isOnSale ? '🔥' : '🛒'}</span>
                              <span>Enroll Now - ₹{calculateTotal(course.price)}</span>
                            </>
                          )}
                        </button>
                        {course.courseType === 'RECORDED' && (
                          <button 
                            onClick={handlePreviewCourse}
                            className="border border-gray-600 text-gray-300 px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-200 font-semibold text-lg"
                          >
                            Preview Free Content
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              
              {enrollmentError && (
                <div className="mt-4 bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
                  {enrollmentError}
                </div>
              )}
              </div>
            </div>
        </div>
      </div>

      {/* What You'll Learn */}
      {course.learningOutcomes && course.learningOutcomes.length > 0 && (
        <div className="bg-gray-900 border-y border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl font-bold text-white mb-6">What you'll learn</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {course.learningOutcomes.map((outcome, index) => (
                <div key={index} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-300">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Live Sessions Schedule (for Live Courses) */}
      {course.courseType === 'LIVE' && liveSessions.length > 0 && (
        <div className="bg-gradient-to-br from-pink-900/20 to-red-900/20 border-y border-pink-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">📅</span>
              <h2 className="text-2xl font-bold text-white">Live Sessions Schedule</h2>
            </div>
            
            {/* Upcoming Sessions */}
            {liveSessions.filter(s => s.status === 'SCHEDULED' && new Date(s.scheduledDate) >= new Date()).length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-sm">Upcoming</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveSessions
                    .filter(s => s.status === 'SCHEDULED' && new Date(s.scheduledDate) >= new Date())
                    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
                    .map((session) => (
                      <div key={session.id} className="bg-gray-800 rounded-lg p-5 border-2 border-gray-700 hover:border-pink-500/50 transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <span className="bg-gradient-to-r from-pink-500 to-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                              Session {session.sessionNumber}
                            </span>
                          </div>
                          <span className="bg-green-900 text-green-300 px-2 py-1 rounded text-xs font-bold">
                            {session.status.replace('_', ' ')}
                          </span>
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{session.title}</h4>
                        {session.description && (
                          <p className="text-gray-400 text-sm mb-3">{session.description}</p>
                        )}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-300">
                            <span>📅</span>
                            <span>{new Date(session.scheduledDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <span>⏱️</span>
                            <span>{session.durationMinutes} minutes</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <span>💻</span>
                            <span>{session.meetingPlatform}</span>
                          </div>
                          {session.topicsCovered && (
                            <div className="flex items-start gap-2 text-gray-300 mt-3 pt-3 border-t border-gray-700">
                              <span>📚</span>
                              <span className="text-xs">{session.topicsCovered}</span>
                            </div>
                          )}
                        </div>
                        {isEnrolled && session.meetingLink && (
                          <a
                            href={session.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 w-full bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white py-2 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2"
                          >
                            <span>🔗</span>
                            <span>Join Meeting</span>
                          </a>
                        )}
                        {!isEnrolled && (
                          <div className="mt-4 w-full bg-gray-700 text-gray-400 py-2 px-4 rounded-lg font-semibold text-sm text-center flex items-center justify-center gap-2">
                            <span>🔒</span>
                            <span>Enroll to Access</span>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Completed Sessions */}
            {liveSessions.filter(s => s.status === 'COMPLETED').length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 py-1 rounded-full text-sm">Completed</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveSessions
                    .filter(s => s.status === 'COMPLETED')
                    .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
                    .slice(0, 4)
                    .map((session) => (
                      <div key={session.id} className="bg-gray-800 rounded-lg p-5 border-2 border-gray-700">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs font-bold">
                              Session {session.sessionNumber}
                            </span>
                          </div>
                          <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                            <span>✓</span>
                            <span>Completed</span>
                          </span>
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{session.title}</h4>
                        <div className="space-y-2 text-sm text-gray-400">
                          <div className="flex items-center gap-2">
                            <span>📅</span>
                            <span>Completed on {new Date(session.scheduledDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        </div>
                        {isEnrolled && session.recordingUrl && (
                          <a
                            href={session.recordingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2"
                          >
                            <span>🎥</span>
                            <span>Watch Recording</span>
                          </a>
                        )}
                        {isEnrolled && !session.recordingUrl && (
                          <div className="mt-4 w-full bg-gray-700 text-gray-400 py-2 px-4 rounded-lg font-semibold text-sm text-center">
                            Recording will be available soon
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* No Sessions Yet */}
            {liveSessions.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📅</div>
                <p className="text-gray-400 text-lg">Sessions will be scheduled soon</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Course Details Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Requirements */}
            {course.requirements && course.requirements.length > 0 && (
              <div className="bg-gray-900 rounded-lg border border-gray-700 p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Requirements</h2>
                <ul className="space-y-3">
                  {course.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <span className="text-gray-500 mt-1">•</span>
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Description */}
            {course.fullDescription && (
              <div className="bg-gray-900 rounded-lg border border-gray-700 p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Description</h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  {course.fullDescription.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Who this course is for */}
            {course.targetAudience && course.targetAudience.length > 0 && (
              <div className="bg-gray-900 rounded-lg border border-gray-700 p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Who this course is for:</h2>
                <ul className="space-y-3">
                  {course.targetAudience.map((audience, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <span className="text-gray-500 mt-1">•</span>
                      <span>{audience}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar - Course Features */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-lg border border-gray-700 p-6 sticky top-24">
              <h3 className="text-lg font-bold text-white mb-6">This course includes:</h3>
              <div className="space-y-4">
                {course.durationHours && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{course.durationHours} hours on-demand video</span>
                  </div>
                )}
                {sections.length > 0 && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{sections.length} sections</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span>Access on mobile and desktop</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Certificate of completion</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Full lifetime access</span>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-700">
                <div className="space-y-2">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Price</span>
                      {course.isOnSale && course.originalPrice ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-sm line-through">₹{course.originalPrice}</span>
                          <span className="text-white text-lg font-semibold">₹{course.price}</span>
                        </div>
                      ) : (
                        <span className="text-white text-lg font-semibold">₹{course.price}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">GST (18%)</span>
                      <span className="text-gray-400 text-sm">₹{calculateGST(course.price)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/20">
                      <span className="text-white font-semibold text-sm">Total</span>
                      <span className="text-green-400 text-lg font-bold">₹{calculateTotal(course.price)}</span>
                    </div>
                  </div>
                  {course.isOnSale && course.discountPercentage && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">Discount</span>
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 py-1 rounded text-xs font-bold">
                        {course.discountPercentage}% OFF
                      </span>
            </div>
                  )}
                  {course.isOnSale && course.discountEndDate && new Date(course.discountEndDate) > new Date() && (
                    <div className="flex items-center justify-between text-xs text-orange-400">
                      <span>⏰ Offer ends:</span>
                      <span>{new Date(course.discountEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  )}
                  {isInstallmentsEnabled(course) && (
                    <div className="mt-4 p-3 rounded-lg bg-purple-900/35 border border-purple-500/40">
                      <p className="text-purple-100 text-sm font-semibold flex items-center gap-2">
                        <span aria-hidden>💳</span>
                        Installments available
                      </p>
                      <p className="text-purple-200/90 text-xs mt-1 leading-relaxed">
                        {hasRegistrationFee(course)
                          ? 'Pay a registration fee first, then the rest in scheduled payments (GST-inclusive total).'
                          : `Split the total into ${Math.min(24, Math.max(2, parseInt(course.installmentCount, 10) || 3))} payments. Select at checkout.`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructor Section */}
      {instructorsForDisplay.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-y border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-16">
            <div className="text-center mb-4 sm:mb-6 lg:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">
                {instructorsForDisplay.length > 1 ? 'Meet Your Instructors' : 'Meet Your Instructor'}
              </h2>
              <div className="w-16 sm:w-20 lg:w-24 h-0.5 sm:h-0.5 lg:h-1 bg-gradient-to-r from-teal-500 to-cyan-500 mx-auto rounded-full"></div>
            </div>

            <div className="space-y-8 sm:space-y-10 lg:space-y-12">
              {instructorsForDisplay.map((inst, instIdx) => (
                <div
                  key={inst.id != null ? inst.id : `inst-${instIdx}`}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10 border border-gray-700/50 shadow-2xl"
                >
                  <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-5 lg:gap-8">
                    <div className="flex-shrink-0 mx-auto lg:mx-0">
                      {inst.imageUrl ? (
                        <div className="relative">
                          <img
                            src={inst.imageUrl}
                            alt={inst.displayName || 'Instructor'}
                            className="w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-48 lg:h-48 rounded-full object-cover border-2 sm:border-3 lg:border-4 border-teal-500 shadow-xl ring-2 sm:ring-3 lg:ring-4 ring-teal-500/20"
                          />
                          <div className="absolute -bottom-1 -right-1 sm:-bottom-1.5 sm:-right-1.5 lg:-bottom-2 lg:-right-2 bg-teal-500 rounded-full p-1 sm:p-1.5 lg:p-2 shadow-lg">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-48 lg:h-48 bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500 rounded-full flex items-center justify-center border-2 sm:border-3 lg:border-4 border-teal-400 shadow-xl ring-2 sm:ring-3 lg:ring-4 ring-teal-500/20">
                            <span className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl text-white font-bold">
                              {inst.displayName ? inst.displayName.charAt(0).toUpperCase() : 'I'}
                            </span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 sm:-bottom-1.5 sm:-right-1.5 lg:-bottom-2 lg:-right-2 bg-teal-500 rounded-full p-1 sm:p-1.5 lg:p-2 shadow-lg">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 text-center lg:text-left w-full">
                      <div className="mb-3 sm:mb-4 lg:mb-4">
                        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-4xl font-bold text-white mb-1.5 sm:mb-2 lg:mb-2 break-words">
                          {inst.displayName || 'Instructor'}
                        </h3>
                        {inst.title && (
                          <div className="flex items-center justify-center lg:justify-start gap-1.5 sm:gap-2 mb-3 sm:mb-4 lg:mb-4">
                            <svg className="w-4 h-4 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <p className="text-teal-400 text-sm sm:text-base md:text-lg lg:text-xl font-semibold break-words">{inst.title}</p>
                          </div>
                        )}
                      </div>

                      {inst.linkedIn && (
                        <div className="mb-4 sm:mb-5 lg:mb-6 flex justify-center lg:justify-start">
                          <a
                            href={inst.linkedIn}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 lg:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                            </svg>
                            <span className="whitespace-nowrap">Connect on LinkedIn</span>
                          </a>
                        </div>
                      )}

                      {inst.bio && (
                        <div className="bg-gray-900/50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 lg:p-6 border border-gray-700/50">
                          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 lg:mb-4">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <h4 className="text-white font-semibold text-sm sm:text-base lg:text-lg">About</h4>
                          </div>
                          <div className="text-gray-300 space-y-1.5 sm:space-y-2 lg:space-y-3 leading-relaxed text-xs sm:text-sm lg:text-base">
                            {inst.bio.split('\n\n').map((paragraph, pIdx) => (
                              <p key={pIdx} className="text-left break-words">{paragraph}</p>
                            ))}
                          </div>
                        </div>
                      )}

                      {!inst.bio && (inst.displayName || inst.title) && (
                        <div className="bg-gray-900/50 rounded-xl p-4 sm:p-6 border border-gray-700/50">
                          <p className="text-gray-400 italic">Instructor information coming soon...</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Course Content */}
      <div id="course-curriculum" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        <div className="bg-gray-900 rounded-lg shadow-sm border border-gray-700">
          <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-8 border-b border-gray-700">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Course Curriculum</h2>
            <p className="text-gray-300 text-sm sm:text-base">Comprehensive learning modules designed for mastery</p>
          </div>
          
          <div className="p-4 sm:p-6 lg:p-8">
          {sections.length === 0 ? (
            <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-700">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No Content Available</h3>
                <p className="text-gray-300 max-w-md mx-auto">
                  Course content is being prepared by the instructor. Please check back later.
              </p>
            </div>
          ) : (
              <>
              <div className="space-y-6">
              {sections
                .map((section, index) => {
                  const sectionQuestions = questions[section.id] || [];
                  const isExpanded = expandedSections.has(section.id);
                  const isFirstSection = index === 0;
                  
                  // NEW LOGIC: 
                  // - If enrolled → everything unlocked
                  // - If not enrolled → only show sections where isPublic=true OR first section
                  const isSectionPublic = section.isPublic === true || section.isFree === true || isFirstSection;
                  const isLocked = !isEnrolled && !isSectionPublic;
                  
                  // Performance optimization: Only show limited sections initially
                  // For live courses: show first 3, for recorded: show first 5
                  // If enrolled, show all sections
                  const shouldShow = (isEnrolled || isSectionPublic) && 
                                    (isEnrolled || index < visibleSectionsCount);
                  
                  if (!shouldShow) {
                    return null;
                  }
                
                return (
                    <div key={section.id} className={`border rounded-lg overflow-hidden ${isLocked ? 'border-orange-400/30 opacity-75' : 'border-gray-700'}`}>
                      <div className="bg-gray-800 px-4 sm:px-6 py-4 sm:py-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white text-black rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-base sm:text-lg font-semibold text-white break-words">
                          {section.title}
                        </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                {isLocked && (
                                  <span className="px-2 sm:px-3 py-1 bg-orange-500/20 border border-orange-400/50 rounded-full text-orange-300 text-xs font-bold flex items-center gap-1 whitespace-nowrap">
                                    🔒 Locked
                                  </span>
                                )}
                                {isFirstSection && !isEnrolled && (
                                  <span className="px-2 sm:px-3 py-1 bg-green-500/20 border border-green-400/50 rounded-full text-green-300 text-xs font-bold flex items-center gap-1 whitespace-nowrap">
                                    ✨ Free Preview
                                  </span>
                                )}
                                </div>
                              </div>
                        {section.description && (
                                <p className="text-gray-300 text-xs sm:text-sm mt-1 break-words">
                            {section.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      {section.isFree && (
                              <span className="bg-green-900 text-green-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-green-700 whitespace-nowrap self-start sm:self-auto">
                                Free
                        </span>
                      )}
                      {(() => {
                        const sectionContents = contents[section.id] || [];
                        const videoCount = sectionContents.filter(c => c.contentType === 'VIDEO').length;
                        const documentCount = sectionContents.filter(c => c.contentType === 'DOCUMENT').length;
                        const questionCount = sectionContents.filter(c => c.contentType === 'QUESTION').length + sectionQuestions.length;
                        
                        return (
                          <div className="flex items-center gap-2 flex-wrap">
                            {videoCount > 0 && (
                                    <span className="bg-red-900 text-red-300 px-2 py-1 rounded text-xs sm:text-sm font-medium border border-red-700 whitespace-nowrap">
                                      {videoCount} Videos
                              </span>
                            )}
                            {documentCount > 0 && (
                                    <span className="bg-blue-900 text-blue-300 px-2 py-1 rounded text-xs sm:text-sm font-medium border border-blue-700 whitespace-nowrap">
                                      {documentCount} Documents
                              </span>
                            )}
                            {questionCount > 0 && (
                                    <span className="bg-green-900 text-green-300 px-2 py-1 rounded text-xs sm:text-sm font-medium border border-green-700 whitespace-nowrap">
                                      {questionCount} Practice
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      <button
                        onClick={() => {
                          if (isEnrolled) {
                            navigate(`/dashboard/courses/${id}`);
                          } else {
                            handlePurchaseClick();
                          }
                        }}
                        className={`w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-lg transition-colors duration-200 text-sm font-medium min-h-[44px] sm:min-h-0 ${
                          isEnrolled
                            ? 'bg-white text-black hover:bg-gray-100'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        {isEnrolled ? '📚 Open in Dashboard' : '🔒 Enroll to Access'}
                      </button>
                          </div>
                    </div>
                    </div>
                    
                    {/* Live Session Display (for live courses) */}
                    {showSectionContentOnPublicPage && course?.courseType === 'LIVE' && !isLocked && (
                      <div className="bg-gray-800 px-4 sm:px-6 py-4 border-t border-gray-700">
                        {liveSessions[section.id] ? (
                          <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-700 rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 mb-3">
                                  <span className="text-xl sm:text-2xl">📅</span>
                                  <h6 className="text-white font-semibold text-base sm:text-lg">Scheduled Live Session</h6>
                                  <div className="flex items-center gap-2">
                                  {liveSessions[section.id].status === 'LIVE_NOW' && (
                                    <span className="bg-red-600 text-white px-2 py-1 rounded-md text-xs font-bold animate-pulse whitespace-nowrap">
                                      🔴 LIVE NOW
                                    </span>
                                  )}
                                  {liveSessions[section.id].status === 'SCHEDULED' && (
                                    <span className="bg-blue-600 text-white px-2 py-1 rounded-md text-xs font-bold whitespace-nowrap">
                                      Upcoming
                                    </span>
                                  )}
                                  {liveSessions[section.id].status === 'COMPLETED' && (
                                    <span className="bg-gray-600 text-white px-2 py-1 rounded-md text-xs whitespace-nowrap">
                                      Completed
                                    </span>
                                  )}
                                  </div>
                                </div>
                                
                                <div className="space-y-2 text-xs sm:text-sm text-gray-200">
                                  <div className="break-words"><strong className="text-purple-400">Title:</strong> {liveSessions[section.id].title}</div>
                                  {liveSessions[section.id].description && (
                                    <div className="break-words"><strong className="text-purple-400">About:</strong> {liveSessions[section.id].description}</div>
                                  )}
                                  <div className="break-words"><strong className="text-purple-400">📍 Date:</strong> {new Date(liveSessions[section.id].scheduledDate).toLocaleString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}</div>
                                  <div><strong className="text-purple-400">⏱️ Duration:</strong> {liveSessions[section.id].durationMinutes} minutes</div>
                                  <div><strong className="text-purple-400">💻 Platform:</strong> {liveSessions[section.id].meetingPlatform}</div>
                                  {liveSessions[section.id].topicsCovered && (
                                    <div className="break-words"><strong className="text-purple-400">📚 Topics:</strong> {liveSessions[section.id].topicsCovered}</div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Join Meeting Button - Only for enrolled users */}
                              <div className="flex flex-col gap-2 sm:flex-shrink-0">
                                {isEnrolled ? (
                                  <>
                                    {liveSessions[section.id].status === 'COMPLETED' ? (
                                      liveSessions[section.id].recordingUrl ? (
                                        <a 
                                          href={liveSessions[section.id].recordingUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-block bg-gray-700 text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200 font-semibold text-center min-h-[44px] flex items-center justify-center"
                                        >
                                          📹 Watch Recording
                                        </a>
                                      ) : (
                                        <span className="text-gray-400 text-xs sm:text-sm text-center">Recording not available</span>
                                      )
                                    ) : (
                                      <a 
                                        href={liveSessions[section.id].meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-semibold shadow-lg text-center min-h-[44px] flex items-center justify-center"
                                      >
                                        {liveSessions[section.id].status === 'LIVE_NOW' ? '🔴 Join Now' : '📹 Join Meeting'}
                                      </a>
                                    )}
                                  </>
                                ) : (
                                  <button 
                                    onClick={handlePurchaseClick}
                                    className="bg-gray-700 text-gray-300 px-4 sm:px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200 font-semibold cursor-pointer min-h-[44px]"
                                  >
                                    🔒 Enroll to Access
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4 text-center">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-yellow-300">
                              <span className="text-xl">⏰</span>
                              <span className="font-semibold text-sm sm:text-base">Live Session Coming Soon</span>
                            </div>
                            <p className="text-yellow-200/80 text-xs sm:text-sm mt-2">
                              The instructor will schedule a live session for this section soon!
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {showSectionContentOnPublicPage && isExpanded && !isLocked && (
                        <div className="p-4 sm:p-6 bg-gray-900">
                          {loadingSections.has(section.id) && (
                            <div className="text-center py-8">
                              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                              <p className="text-gray-400 mt-4">Loading content...</p>
                            </div>
                          )}
                          {!loadingSections.has(section.id) && (
                            <>
                          {/* Legacy Questions Section */}
                        {(() => {
                          // NEW QUESTION FILTER LOGIC:
                          // - Enrolled users: See ALL questions
                          // - Non-enrolled users: See only questions where isPublic=true
                          const visibleQuestions = isEnrolled 
                            ? sectionQuestions 
                            : sectionQuestions.filter(q => q.isPublic === true);
                          
                          if (visibleQuestions.length === 0) return null;
                          
                          return (
                            <div className="mb-6 sm:mb-8">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                                  <h4 className="text-base sm:text-lg font-semibold text-white">Practice Questions</h4>
                                  <div className="flex items-center gap-2 flex-wrap">
                                  <span className="bg-green-900 text-green-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-green-700 whitespace-nowrap">
                                  {visibleQuestions.length} Questions
                                </span>
                                {!isEnrolled && visibleQuestions.length < sectionQuestions.length && (
                                  <span className="bg-yellow-900 text-yellow-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-yellow-700 whitespace-nowrap">
                                    Preview Mode
                                  </span>
                                )}
                                  </div>
                              </div>
                              <div className="space-y-4">
                                {visibleQuestions.map((question, qIndex) => (
                                  <div key={question.id} className="border border-gray-700 rounded-lg p-4 sm:p-6 bg-gray-800">
                                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        <span className="bg-gray-700 text-gray-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium self-start sm:self-auto">
                                        #{question.questionOrder || qIndex + 1}
                                      </span>
                                        <h5 className="text-white font-semibold text-sm sm:text-base break-words">{question.title}</h5>
                                      {question.isPremium && (
                                          <span className="bg-yellow-900 text-yellow-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-yellow-700 whitespace-nowrap self-start sm:self-auto">
                                            Premium
                                        </span>
                                      )}
                                    </div>
                                      <span className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border ${getDifficultyColor(question.difficultyLevel)} self-start sm:self-auto whitespace-nowrap`}>
                                      {question.difficultyLevel}
                                    </span>
                                  </div>
                                  
                                    <p className="text-gray-300 mb-4 text-sm sm:text-base break-words">{question.description}</p>
                                  
                                  {question.problemStatement && (
                                    <div className="mb-4">
                                        <p className="text-white font-semibold mb-2 text-sm sm:text-base">Problem Statement:</p>
                                        <div className="bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-600 overflow-x-auto">
                                          <p className="text-gray-300 font-mono text-xs sm:text-sm whitespace-pre-wrap break-words">{question.problemStatement}</p>
                                      </div>
                                    </div>
                                  )}
                                  
                                    {question.examples && (
                                    <div className="mb-4">
                                        <p className="text-white font-semibold mb-2 text-sm sm:text-base">Examples:</p>
                                        <div className="bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-600 overflow-x-auto">
                                          <p className="text-gray-300 font-mono text-xs sm:text-sm whitespace-pre-wrap break-words">{question.examples}</p>
                                    </div>
                                    </div>
                                  )}
                                  
                                  {question.constraints && (
                                    <div className="mb-4">
                                        <p className="text-white font-semibold mb-2 text-sm sm:text-base">Constraints:</p>
                                        <div className="bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-600 overflow-x-auto">
                                          <p className="text-gray-300 font-mono text-xs sm:text-sm whitespace-pre-wrap break-words">{question.constraints}</p>
                                      </div>
                                    </div>
                                  )}
                                  
                                    <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-gray-400 mb-4">
                                    {question.timeLimitSeconds && (
                                        <span>Time Limit: {question.timeLimitSeconds}s</span>
                                    )}
                                    {question.memoryLimitMB && (
                                        <span>Memory Limit: {question.memoryLimitMB}MB</span>
                                    )}
                                  </div>
                                  
                                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    <Link
                                      to={`/question-ide?question=${question.id}&title=${encodeURIComponent(question.title)}&problem=${encodeURIComponent(question.problemStatement || question.description || '')}&solution=${encodeURIComponent(question.solutionCode || question.solution || '')}&initialCode=${encodeURIComponent(question.initialCode || '')}&sourceLanguage=${encodeURIComponent(question.sourceLanguage || 'java')}&examples=${encodeURIComponent(question.examples || '')}&constraints=${encodeURIComponent(question.constraints || '')}&timeLimit=${question.timeLimitSeconds || 1000}&memoryLimit=${question.memoryLimitMB || 256}`}
                                        className="bg-white text-black px-4 py-2.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium text-center min-h-[44px] flex items-center justify-center text-sm"
                                    >
                                        Practice in IDE
                                    </Link>
                                    {question.videoLink && (
                                      <a 
                                        href={question.videoLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                          className="border border-gray-600 text-gray-300 px-4 py-2.5 sm:py-2 rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium text-center min-h-[44px] flex items-center justify-center text-sm"
                                      >
                                          Watch Solution
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          );
                        })()}
                        
                          {/* Course Content */}
                        {(() => {
                          const sectionContents = contents[section.id] || [];
                          
                          if (sectionContents.length > 0) {
                            // NEW PREVIEW LOGIC:
                            // - Enrolled users: See ALL content
                            // - Non-enrolled users: See only content where isFree=true
                            const visibleContents = isEnrolled 
                              ? sectionContents 
                              : sectionContents.filter(c => c.isFree === true);
                            const hasHiddenContent = !isEnrolled && visibleContents.length < sectionContents.length;
                            
                            return (
                                <div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                                    <h4 className="text-base sm:text-lg font-semibold text-white">Course Content</h4>
                                    <div className="flex items-center gap-2 flex-wrap">
                                    <span className="bg-gray-700 text-gray-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap">
                                  {sectionContents.length} Items
                                </span>
                                    {!isEnrolled && (
                                      <span className="bg-yellow-900 text-yellow-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-yellow-700 whitespace-nowrap">
                                        Preview Mode
                                      </span>
                                    )}
                                    </div>
                              </div>
                                <div className="space-y-4">
                                  {visibleContents.map((content, cIndex) => (
                                      <div key={content.id} className="border border-gray-700 rounded-lg p-4 sm:p-6 bg-gray-800">
                                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                            <span className="bg-gray-700 text-gray-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium self-start sm:self-auto">
                                            #{content.contentOrder || cIndex + 1}
                                          </span>
                                            <h5 className="text-white font-semibold text-sm sm:text-base break-words">{content.title}</h5>
                                            <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border ${getContentTypeColor(content.contentType)} whitespace-nowrap`}>
                                              {getContentTypeIcon(content.contentType)}
                                          </span>
                                          {content.isPremium && (
                                              <span className="bg-yellow-900 text-yellow-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-yellow-700 whitespace-nowrap">
                                                Premium
                                            </span>
                                          )}
                                            </div>
                                        </div>
                                        {content.isFree && (
                                            <span className="bg-green-900 text-green-300 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border border-green-700 whitespace-nowrap self-start sm:self-auto">
                                              Free
                                          </span>
                                        )}
                                      </div>
                                      
                                        <p className="text-gray-300 mb-4 text-sm sm:text-base break-words">{content.description}</p>
                                      
                                      {/* Video Content */}
                                      {content.contentType === 'VIDEO' && content.videoUrl && (
                                        <div className="mb-4">
                                            {content.videoDuration && (
                                              <div className="text-sm text-gray-400 mb-3">
                                                Duration: {formatDuration(content.videoDuration)}
                                          </div>
                                            )}
                                          
                                          {/* Use VideoPlayer for ALL videos (local and Cloudinary) */}
                                          <VideoPlayer 
                                            videoUrl={content.videoUrl}
                                            title={content.title}
                                            description={content.description}
                                            isUploaded={true}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Document Content */}
                                      {content.contentType === 'DOCUMENT' && content.documentUrl && (
                                        <div className="mb-4">
                                            <div className="text-sm text-gray-400 mb-3">
                                              {content.documentType && `${content.documentType} • `}
                                              {content.documentSize && `${formatFileSize(content.documentSize)}`}
                                          </div>
                                          <a 
                                            href={content.documentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                              className="bg-white text-black px-4 py-2.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium text-center min-h-[44px] flex items-center justify-center text-sm"
                                          >
                                              Download Document
                                          </a>
                                        </div>
                                      )}
                                      
                                        {/* Question Content */}
                                        {content.contentType === 'QUESTION' && content.question && (
                                          <div className="mb-4">
                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                                              {content.question.difficultyLevel && (
                                                <span className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium border ${getDifficultyColor(content.question.difficultyLevel)} whitespace-nowrap`}>
                                                  {content.question.difficultyLevel}
                                                </span>
                                              )}
                                              {content.question.timeLimitSeconds && (
                                                <span className="text-gray-400 text-xs sm:text-sm">Time: {content.question.timeLimitSeconds}s</span>
                                              )}
                                            </div>
                                            
                                            {content.question.problemStatement && (
                                              <div className="mb-4">
                                                <p className="text-white font-semibold mb-2 text-sm sm:text-base">Problem Statement:</p>
                                                <div className="bg-gray-800 p-3 sm:p-4 rounded-lg border border-gray-600 overflow-x-auto">
                                                  <p className="text-gray-300 font-mono text-xs sm:text-sm whitespace-pre-wrap break-words">{content.question.problemStatement}</p>
                                                </div>
                                              </div>
                                            )}
                                            
                                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                              <Link
                                                to={`/question-ide?questionId=${content.question.id}`}
                                                className="bg-white text-black px-4 py-2.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium text-center min-h-[44px] flex items-center justify-center text-sm"
                                              >
                                                Practice in IDE
                                              </Link>
                                              {content.question.videoLink && (
                                                <a 
                                                  href={content.question.videoLink}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="border border-gray-600 text-gray-300 px-4 py-2.5 sm:py-2 rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium text-center min-h-[44px] flex items-center justify-center text-sm"
                                                >
                                                  Watch Solution
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        )}
                </div>
              ))}
                                  
                                  {/* Show enrollment prompt for hidden content */}
                                  {hasHiddenContent && (
                                    <div className="border-2 border-dashed border-yellow-700 rounded-lg p-4 sm:p-6 bg-yellow-900/20 text-center">
                                      <div className="text-yellow-300 mb-2">
                                        <span className="text-2xl sm:text-3xl">🔒</span>
                                      </div>
                                      <h5 className="text-white font-semibold mb-2 text-sm sm:text-base">
                                        {sectionContents.length - visibleContents.length} More {sectionContents.length - visibleContents.length === 1 ? 'Item' : 'Items'} Locked
                                      </h5>
                                      <p className="text-gray-300 mb-4 text-xs sm:text-sm">
                                        Enroll in this course to access all content and live sessions
                                      </p>
                                      <button 
                                        onClick={handlePurchaseClick}
                                        className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all duration-200 font-semibold min-h-[44px] text-sm sm:text-base"
                                      >
                                        Unlock All Content
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                            </>
                          )}
                      </div>
                    )}
                  </div>
                );
              })
              .filter(Boolean)} {/* Remove null entries */}
            </div>
            
            {/* Load More Sections Button - Only show if there are more sections */}
            {!isEnrolled && sections.filter((section, index) => {
              const isFirstSection = index === 0;
              const isSectionPublic = section.isPublic === true || section.isFree === true || isFirstSection;
              return isSectionPublic && index >= visibleSectionsCount;
            }).length > 0 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setVisibleSectionsCount(prev => prev + (course?.courseType === 'LIVE' ? 3 : 5))}
                  className="px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Load More Sections ({sections.filter((section, index) => {
                    const isFirstSection = index === 0;
                    const isSectionPublic = section.isPublic === true || section.isFree === true || isFirstSection;
                    return isSectionPublic && index >= visibleSectionsCount;
                  }).length} remaining)
                </button>
              </div>
            )}
              </>
          )}
            </div>
          </div>
        </div>

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4" onClick={() => setShowLoginModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-orange-400/50 shadow-2xl shadow-orange-500/30" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-2xl font-bold text-white mb-2">Login Required</h3>
              <p className="text-gray-300 text-sm mb-4">
                You need to be logged in to enroll in this course. Please login or create an account to continue.
              </p>
            </div>

            <div className="bg-orange-900/30 border border-orange-400/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <span className="text-2xl">ℹ️</span>
                <div>
                  <p className="text-orange-300 text-xs font-bold mb-1">Why Login?</p>
                  <p className="text-gray-300 text-xs">Logging in allows us to track your progress, save your work, and provide you with certificates upon completion.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setShowAuthModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-orange-500/50 transform hover:scale-105"
              >
                🚀 Go to Login
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && course && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4" onClick={() => setShowPurchaseModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-white/20 shadow-2xl shadow-purple-500/30 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🎓</div>
              <h3 className="text-2xl font-bold text-white mb-1">Enroll in Course</h3>
              <p className="text-gray-300 text-sm">Complete your enrollment to unlock all content</p>
            </div>

            <div className="bg-white/10 rounded-xl p-4 mb-4 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3">{course.title}</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Instructor:</span>
                  <span className="text-white font-semibold text-sm">{course.instructor}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Category:</span>
                  <span className="text-purple-400 font-semibold text-sm">{course.category}</span>
                </div>
                {course.durationHours && (
                  <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">Duration:</span>
                    <span className="text-white font-semibold text-sm">{course.durationHours}h</span>
                  </div>
                )}
                {course.studentsEnrolled && (
                  <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">Students:</span>
                    <span className="text-white font-semibold text-sm">{course.studentsEnrolled.toLocaleString()}</span>
                  </div>
                )}
                <div className="pt-3 mt-2 border-t-2 border-white/20">
                  {course.isOnSale && course.originalPrice ? (
                    <div className="space-y-2">
                  <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Original Price:</span>
                        <span className="text-gray-400 text-sm line-through">₹{course.originalPrice}</span>
                      </div>
                      {course.discountPercentage && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Discount:</span>
                          <span className="text-red-400 font-bold text-sm">{course.discountPercentage}% OFF</span>
                        </div>
                      )}
                      <div className="space-y-2 pt-2 border-t border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold text-sm">Price:</span>
                          <span className="text-white font-semibold text-sm">₹{course.price}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">GST (18%):</span>
                          <span className="text-gray-400 text-sm">₹{calculateGST(course.price)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-white font-semibold text-sm">Total Price:</span>
                          <span className="text-green-400 font-bold text-lg">₹{calculateTotal(course.price)}</span>
                        </div>
                      </div>
                      {course.offerDescription && (
                        <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-2 mt-2">
                          <p className="text-orange-300 text-xs text-center">⚡ {course.offerDescription}</p>
                </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm">Price:</span>
                        <span className="text-white font-semibold text-sm">₹{course.price}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">GST (18%):</span>
                        <span className="text-gray-400 text-sm">₹{calculateGST(course.price)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-white/20">
                        <span className="text-white font-semibold text-sm">Total Price:</span>
                        <span className="text-green-400 font-bold text-lg">₹{calculateTotal(course.price)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isInstallmentsEnabled(course) && (
              <div className="mb-4 p-4 rounded-xl bg-purple-900/40 border border-purple-500/30 text-left">
                <p className="text-white font-semibold text-sm mb-2">Payment option</p>
                <label className="flex items-start gap-2 mb-2 cursor-pointer">
                  <input
                    type="radio"
                    name="payMode"
                    checked={checkoutPaymentMode === 'FULL'}
                    onChange={() => setCheckoutPaymentMode('FULL')}
                    className="mt-1"
                  />
                  <span className="text-gray-200 text-sm">
                    Pay in full — ₹{calculateTotal(course.price)} (incl. GST)
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="payMode"
                    checked={checkoutPaymentMode === 'INSTALLMENT'}
                    onChange={() => setCheckoutPaymentMode('INSTALLMENT')}
                    className="mt-1"
                  />
                  <span className="text-gray-200 text-sm">
                    {hasRegistrationFee(course)
                      ? `Registration fee first, then ${Math.min(24, Math.max(1, parseInt(course.installmentCount, 10) || 1))} installment${(parseInt(course.installmentCount, 10) || 1) !== 1 ? 's' : ''} (full course total incl. GST is split accordingly).`
                      : `Pay in ${Math.min(24, Math.max(2, course.installmentCount || 3))} equal installments (GST included in total). Pay the first today, then the rest from your dashboard.`}
                  </span>
                </label>
                {checkoutPaymentMode === 'INSTALLMENT' && (
                  <ul className="mt-2 text-xs text-purple-200 space-y-1 list-disc list-inside">
                    {buildCoursePaymentSchedule(course).map((amt, idx) => (
                      <li key={idx}>
                        {hasRegistrationFee(course) && idx === 0
                          ? `Registration (due now): ₹${amt.toFixed(2)}`
                          : `Payment ${idx + 1}: ₹${amt.toFixed(2)}`}
                      </li>
                    ))}
                  </ul>
                )}
                {course.installmentNotes && (
                  <p className="text-gray-400 text-xs mt-2">{course.installmentNotes}</p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handlePurchase}
                disabled={enrolling}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enrolling ? '⏳ Processing...' : '✅ Proceed to Enrollment'}
              </button>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccess && paymentDetails && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPaymentSuccess(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            {/* Success Icon with Animation */}
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-8 rounded-t-2xl text-center relative overflow-hidden">
              {/* Animated Background Circles */}
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white/10 rounded-full animate-ping"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white/20 rounded-full"></div>
              </div>
              
              {/* Success Icon */}
              <div className="relative z-10 mb-4">
                <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h2 className="relative z-10 text-3xl font-bold text-white mb-2">
                {paymentDetails.isAdmin ? '✅ Admin Access Granted!' : '🎉 Enrollment Successful!'}
              </h2>
              <p className="relative z-10 text-green-100 text-sm">
                {paymentDetails.isAdmin ? 'You have administrator access' : 'Your enrollment has been completed'}
              </p>
            </div>

            {/* Payment Details */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 mb-5 space-y-3">
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Course</span>
                  <span className="text-gray-900 font-bold text-sm text-right max-w-[60%]">
                    {paymentDetails.courseName}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Amount Paid</span>
                  <span className="text-green-600 font-bold text-base">
                    ₹{paymentDetails.amount}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Transaction ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.transactionId}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2">
                  <span className="text-gray-600 font-medium text-sm">Order ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.orderId}
                  </span>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="text-green-900 font-semibold text-sm mb-1">
                      You can now access all course content!
                    </p>
                    <p className="text-green-700 text-xs">
                      {paymentDetails.isAdmin 
                        ? 'All content is unlocked for administrators.' 
                        : 'Start learning with videos, articles, and practice questions.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowPaymentSuccess(false)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105"
              >
                Awesome! 🎉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </div>
  );
};

export default CourseDetail; 

